import { useIsDesktop } from "common/MediaQueries"
import NextImage from "common/bit/NextImage"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
const TOTAL_FRAMES = 24

const StyledWrapper = styled.div`
  /* height: 100%; */
  aspect-ratio: 16 / 9;
  position: relative;
  flex-grow: 1;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 110%;
    flex-grow: 0;
    // aspect-ratio: 15 / 9;
    margin-bottom: calc(-1% * 15 / 9);
    overflow: hidden;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    max-width: 100vw;
    height: 0;
  }
`
const StyledImagesContainer = styled.div<{ "data-a11y"?: string }>`
  overflow: hidden;
  cursor: grab;
  width: 100%;
  height: 100%;
  &:focus-visible {
    :after {
      // contact from aria-label
      content: "${(p) => p["data-a11y"]}";
      position: absolute;
      top: 0;
      right: 0;
      padding: 5px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
    }
  }
`

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  ${(p) => p.theme.breakpoints.down("sm")} {
    /* margin-top: 40px; */
  }
`

export const StyledImage = styled.img`
  width: 100%;
  display: block;
  height: 100%;
  object-fit: cover;
`

const StyledThreeSixtyIcon = styled.img`
  width: 32px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  height: 32px;
  cursor: grab;
  user-select: none;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 50px;
    transform: translate(-50%, 50%);
  }
`

interface ThreeSixtyViewerProps {
  alt: string
  imageList: string[]
  className?: string
  enable?: boolean
  showIcon?: boolean
}
const ThreeSixtyViewer = ({
  imageList,
  alt,
  className,
  enable = true,
  showIcon = false,
}: ThreeSixtyViewerProps) => {
  const [activeImage, setActiveImage] = useState(0)
  const [enableSpin, setEnableSpin] = useState(0)
  const [activeImageLoaded, setActiveImageLoaded] = useState(false)
  const [circlrInstance, setCirclrInstance] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [prevX, setPrevX] = useState(0)
  const isDesktop = useIsDesktop()

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    setIsDragging(true)
    setPrevX(e.clientX)
  }

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !circlrInstance) return

      const deltaX = e.clientX - prevX
      if (deltaX > 0) {
        circlrInstance.next()
      } else if (deltaX < 0) {
        circlrInstance.prev()
      }
      setPrevX(e.clientX)
    },
    [isDragging, circlrInstance, prevX]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const images = useMemo(() => {
    const _images = imageList || [] // Ensure imageList is always an array
    return _images.length - 1 === TOTAL_FRAMES
      ? _images
      : [
          ..._images,
          ...Array<string>(TOTAL_FRAMES - _images.length).fill(
            _images[0] || ""
          ),
        ]
  }, [imageList])

  const imagesContainerRef = useCallback(
    (node) => {
      if (!node || !enableSpin) return
      import("circlr").then(({ default: circlr }) => {
        const circlrObj = circlr(node)
          .scroll(false)
          .on("show", (i) => {
            setActiveImage(i)
          })
        setCirclrInstance(circlrObj)
      })
    },
    [enableSpin]
  )

  useEffect(() => {
    setActiveImageLoaded(false)
  }, [imageList])

  useEffect(() => {
    if (enable) {
      setEnableSpin((prev) => prev + 1)
    }
  }, [enable])

  const getImageProps = (image: string) =>
    isDesktop
      ? { src: `${image}?width=760` }
      : {
          src: `${image}?width=375`,
          sizes: "100vw",
          srcSet: `${image}?width=375 1x,
                   ${image}?width=750 2x,
                   ${image}?width=1125 3x`,
        }

  return (
    <StyledWrapper className={className}>
      <StyledImagesContainer
        ref={imagesContainerRef}
        style={{ pointerEvents: !enable ? "none" : "auto" }}
        tabIndex={!enable ? -1 : 0}
        role="application"
        data-a11y="רכיב קרוסלת 360 מעלות. ניתן לשנות תמונה על ידי שימוש בחצי המקלדת ימינה ושמאלה"
        id="three-sixty-viewer"
        onKeyDown={(e) => {
          e.stopPropagation()
          // e.preventDefault()
          if (e.key === "ArrowLeft") {
            circlrInstance?.next()
          } else if (e.key === "ArrowRight") {
            circlrInstance?.prev()
          }
        }}
      >
        {images.map((image, i) =>
          i === 0 ? (
            <ImageWrap key={i}>
              <NextImage
                image={{
                  imageUrl: images[0],
                  // imageUrl: image,
                  alt: `${alt}=0`,
                }}
                imageSizes={["100vw", "760px"]}
                priority
                loadingEager={enableSpin && activeImageLoaded ? true : false}
                onLoadCallBack={() => {
                  if (activeImage === i) {
                    setActiveImageLoaded(true)
                  }
                }}
              />
            </ImageWrap>
          ) : (
            !!enableSpin && (
              <ImageWrap key={i}>
                <StyledImage
                  {...getImageProps(image)}
                  alt={`${alt}=${i}`}
                  loading={enableSpin && activeImageLoaded ? "eager" : "lazy"}
                  onLoad={() => {
                    if (activeImage === i) {
                      setActiveImageLoaded(true)
                    }
                  }}
                />
              </ImageWrap>
            )
          )
        )}
      </StyledImagesContainer>
      {showIcon && (
        <StyledThreeSixtyIcon
          src="/icons/rotate-icon.svg"
          alt=""
          title="לחץ וגרור לסובב רכב"
          onMouseDown={handleMouseDown}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        />
      )}
      {/* we nned to deside if to pass this or not.. */}
    </StyledWrapper>
  )
}

export default memo(ThreeSixtyViewer)
